import React, { useState } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';

const FormAnalyticsTour = ({ run, onComplete }) => {
  const [stepIndex, setStepIndex] = useState(0);

  const steps = [
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Welcome to Form Analytics! 📊</h3>
          <p className="text-gray-700 mb-3">
            This is where you track your form's performance and analyze responses. Let's explore the key features!
          </p>
          <p className="text-sm text-gray-500">
            You can skip this tour anytime by clicking "Skip" or pressing ESC.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="analytics-tabs"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Analytics Tabs</h3>
          <p className="text-gray-700 mb-2">
            Switch between Overview and Responses to see different insights:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li><strong>Overview</strong> - Charts, metrics, and question breakdowns</li>
            <li><strong>Responses</strong> - Individual submission details</li>
          </ul>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="share-button"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Share Your Form</h3>
          <p className="text-gray-700 mb-2">
            Click here to copy your form link and share it anywhere to collect more responses!
          </p>
          <p className="text-sm text-blue-600">
            💡Tip: Share on social media, email, or embed on your website.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="metrics-cards"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Key Metrics</h3>
          <p className="text-gray-700 mb-2">
            Track important statistics about your form performance:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li><strong>Total Responses</strong> - How many people submitted</li>
            <li><strong>Completion Rate</strong> - Success rate of submissions</li>
            <li><strong>Avg Time</strong> - How long users take to complete</li>
            <li><strong>Engagement Score</strong> - Overall user interaction quality</li>
          </ul>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="response-chart"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Response Trends</h3>
          <p className="text-gray-700 mb-2">
            Visualize how your responses grow over time. Track daily submission patterns and identify peak periods.
          </p>
          <p className="text-sm text-blue-600">
            💡This helps you understand when your audience is most active!
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="question-breakdown"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Question Breakdown</h3>
          <p className="text-gray-700 mb-2">
            Deep dive into each question's analytics:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li>View response distribution for multiple choice questions</li>
            <li>See average ratings and scores</li>
            <li>Read all text responses in one place</li>
            <li>Track response rates for each question</li>
          </ul>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="export-csv"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Export to CSV</h3>
          <p className="text-gray-700 mb-2">
            Download all your responses as a CSV file for further analysis in Excel, Google Sheets, or other tools.
          </p>
          <p className="text-sm text-blue-600">
            💡Perfect for creating reports or sharing data with your team!
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="response-list"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Individual Responses</h3>
          <p className="text-gray-700 mb-2">
            View detailed submissions with:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li>All answers from each respondent</li>
            <li>Submission timestamp and time to complete</li>
            <li>XP points earned (if gamification is enabled)</li>
            <li>Device and browser information</li>
          </ul>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">You're Ready to Analyze! 🎉</h3>
          <p className="text-gray-700 mb-3">
            Now you know how to track, analyze, and export your form data. Use these insights to make data-driven decisions!
          </p>
          <p className="text-sm text-gray-600 mb-2">
            Need help? Click the help button anytime to restart this tour.
          </p>
          <p className="text-sm text-blue-600">
            Happy analyzing!
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status, action, index, type } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Tour completed or skipped
      if (onComplete) {
        onComplete();
      }
    } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      // Move to next step
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(nextStepIndex);
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      stepIndex={stepIndex}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#0066FF',
          textColor: '#374151',
          backgroundColor: '#FFFFFF',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          arrowColor: '#FFFFFF',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 12,
          padding: 20,
          fontSize: 14,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#0066FF',
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: 14,
          fontWeight: 600,
        },
        buttonBack: {
          color: '#6B7280',
          marginRight: 10,
        },
        buttonSkip: {
          color: '#9CA3AF',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      disableScrolling={false}
      spotlightPadding={4}
      disableOverlayClose
      hideCloseButton={false}
    />
  );
};

export default FormAnalyticsTour;
